import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/content";
import { ease } from "@/components/Reveal";

export const Logo = ({ light = true }) => (
  <Link to="/" data-testid="logo-link" className="group flex items-center gap-3">
    <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-1 shadow-[0_8px_24px_rgba(108,59,255,0.25)] transition-transform duration-300 group-hover:scale-105">
      <img
        src="/pfs-logo-full.png"
        alt="PFS — Protech Future System logo"
        className="h-full w-full object-contain"
        data-testid="logo-emblem"
      />
    </span>
    <span className="leading-none">
      <span className={`block font-display text-base font-extrabold tracking-[0.18em] ${light ? "text-white" : "text-navy"}`}>
        PROTECH
      </span>
      <span className={`mt-1 block text-[10px] font-semibold uppercase tracking-[0.32em] ${light ? "text-white/50" : "text-steel"}`}>
        Future System Sdn Bhd
      </span>
    </span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "border-b border-white/5 bg-midnight/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-6 lg:px-8">
        <Logo />

        <nav className="hidden items-center gap-9 lg:flex" data-testid="nav-desktop">
          {NAV_LINKS.map((link) => {
            const active = pathname === link.path || (link.path === "/" && pathname === "/home");
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={link.testId}
                className={`relative font-display text-sm font-bold tracking-wide transition-colors duration-200 ${
                  active ? "text-aqua" : "text-white/70 hover:text-white"
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-2 left-0 h-0.5 bg-gradient-to-r from-iris to-aqua transition-all duration-300 ${
                    active ? "w-full" : "w-0"
                  }`}
                />
              </Link>
            );
          })}
          <Link
            to="/contact-us"
            data-testid="navbar-cta-button"
            className="group inline-flex items-center gap-2 rounded-full bg-iris px-6 py-2.5 font-display text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-aqua hover:text-midnight"
          >
            Talk to Us
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          type="button"
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle navigation menu"
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 text-white lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "calc(100vh - 5rem)" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease }}
            className="overflow-hidden bg-midnight/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex h-full flex-col justify-between px-6 pb-10 pt-6">
              <div className="flex flex-col gap-2">
                {NAV_LINKS.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.07, duration: 0.45, ease }}
                  >
                    <Link
                      to={link.path}
                      data-testid={`${link.testId}-mobile`}
                      className="flex items-center justify-between border-b border-white/10 py-4 font-display text-2xl font-bold text-white"
                    >
                      {link.label}
                      <ArrowUpRight className="h-5 w-5 text-aqua" />
                    </Link>
                  </motion.div>
                ))}
              </div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.45, ease }}
                className="space-y-2"
              >
                <a href={`mailto:${COMPANY.email}`} data-testid="mobile-menu-email" className="block text-sm text-white/60">
                  {COMPANY.email}
                </a>
                <a href={COMPANY.phoneHref} data-testid="mobile-menu-phone" className="block text-sm text-white/60">
                  {COMPANY.phoneDisplay}
                </a>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
